import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';
import VacacionMapEmbed from '../../components/public/VacacionMapEmbed.jsx';
import { getMapEmbedSrc } from '../../utils/mapEmbed.js';

function emptyForm() {
  return {
    titulo: '',
    descripcion: '',
    ubicacion: '',
    mapa_embed: '',
    precio: 0,
    imagen: '',
    galeria: [],
    fecha_inicio: '',
    fecha_fin: '',
    estado: true,
  };
}

export default function VacacionesAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploading } = useUpload();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: emptyForm(),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'galeria' });
  const galeriaPaths = watch('galeria');
  const mapaEmbedValue = watch('mapa_embed');
  const mapPreviewSrc = getMapEmbedSrc(mapaEmbedValue);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/vacaciones', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    const today = new Date().toISOString().slice(0, 10);
    reset({
      ...emptyForm(),
      fecha_inicio: today,
      fecha_fin: today,
      galeria: [],
    });
    setModalOpen(true);
  }

  async function openEdit(row) {
    setEditingId(row.id);
    const gal = Array.isArray(row.galeria) ? row.galeria.map((p) => ({ path: p })) : [];
    reset({
      titulo: row.titulo ?? '',
      descripcion: row.descripcion ?? '',
      ubicacion: row.ubicacion ?? '',
      mapa_embed: row.mapa_embed ?? '',
      precio: Number(row.precio) || 0,
      imagen: row.imagen ?? '',
      galeria: gal,
      fecha_inicio: String(row.fecha_inicio ?? '').slice(0, 10),
      fecha_fin: String(row.fecha_fin ?? '').slice(0, 10),
      estado: Number(row.estado) === 1,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      titulo: values.titulo,
      descripcion: values.descripcion,
      ubicacion: values.ubicacion,
      mapa_embed: values.mapa_embed,
      precio: Number(values.precio),
      imagen: values.imagen,
      galeria: (values.galeria || []).map((x) => x.path).filter(Boolean),
      fecha_inicio: values.fecha_inicio,
      fecha_fin: values.fecha_fin,
      estado: values.estado ? 1 : 0,
    };

    try {
      if (editingId) {
        const { data } = await api.put(`/v1/vacaciones/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/vacaciones', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar programa?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/vacaciones/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleEstado(row) {
    try {
      const current = await api.get(`/v1/vacaciones/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/vacaciones/${row.id}`, {
        ...r,
        estado: Number(r.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickGallery(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      append({ path });
      toastSuccess('Imagen añadida a la galería', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  async function onPickCover(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Portada lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      { key: 'titulo', label: 'Título' },
      { key: 'ubicacion', label: 'Ubicación' },
      {
        key: 'precio',
        label: 'Precio',
        render: (row) =>
          new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(
            Number(row.precio)
          ),
      },
      {
        key: 'estado',
        label: 'Estado',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Activo' : 'Pausado'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Sedes vacacionales</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Gestiona las opciones de estadía en Tocaima (habitaciones y casas). El precio en listado es referencia “desde”; las tarifas completas están en el reglamento público.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo programa
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar programa' : 'Nuevo programa'} wide>
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Título" error={errors.titulo?.message}>
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('titulo', { required: 'Requerido' })}
            />
          </FormField>
          <FormField label="Ubicación (texto)">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              placeholder="Ej. Tocaima, Cundinamarca"
              {...register('ubicacion')}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField
              label="Mapa (iframe Google Maps)"
              hint="Pega la URL del embed (src) o el código iframe completo desde Google Maps → Compartir → Insertar un mapa."
            >
              <textarea
                rows={4}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 font-mono text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                placeholder='https://www.google.com/maps/embed?pb=... o <iframe src="..." ...></iframe>'
                {...register('mapa_embed')}
              />
            </FormField>
            {mapPreviewSrc ? (
              <div className="mt-3 overflow-hidden rounded-xl ring-1 ring-white/15">
                <div className="aspect-video w-full min-h-[200px]">
                  <iframe
                    src={mapPreviewSrc}
                    title="Vista previa del mapa"
                    className="h-full w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            ) : mapaEmbedValue?.trim() ? (
              <p className="mt-2 text-xs text-amber-400">
                No se reconoció una URL de Google Maps válida. Revisa el código pegado.
              </p>
            ) : null}
          </div>
          <FormField label="Precio (COP)" error={errors.precio?.message}>
            <input
              type="number"
              step="1"
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('precio', { valueAsNumber: true })}
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Inicio">
              <input
                type="date"
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('fecha_inicio')}
              />
            </FormField>
            <FormField label="Fin">
              <input
                type="date"
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('fecha_fin')}
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Descripción">
              <textarea
                rows={4}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('descripcion')}
              />
            </FormField>
          </div>

          <div className="md:col-span-2">
            <FormField label="Imagen portada" hint={uploading ? 'Subiendo…' : undefined}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input type="file" accept="image/*" onChange={onPickCover} disabled={uploading} />
                <input
                  type="text"
                  className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                  {...register('imagen')}
                />
              </div>
            </FormField>
          </div>

          <div className="md:col-span-2 space-y-3 rounded-xl border border-white/10 bg-brand-navy/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Galería</p>
                <p className="text-xs text-slate-500">Sube imágenes adicionales; aparecen como lista de rutas.</p>
              </div>
              <input type="file" accept="image/*" onChange={onPickGallery} disabled={uploading} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-slate-950/50 p-2"
                >
                  <div className="h-14 w-20 overflow-hidden rounded-md bg-black/40">
                    {galeriaPaths?.[index]?.path ? (
                      <img src={assetUrl(galeriaPaths[index].path)} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <input
                    type="text"
                    className="flex-1 rounded-lg border border-white/15 bg-slate-950/60 px-2 py-2 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                    {...register(`galeria.${index}.path`)}
                  />
                  <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={() => remove(index)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {fields.length === 0 && <p className="text-sm text-slate-500">Aún no hay imágenes en galería.</p>}
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-slate-300 md:col-span-2">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green" {...register('estado')} />
            Activo en web pública
          </label>

          <div className="flex flex-wrap gap-3 md:col-span-2">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear programa'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa" wide>
        {previewRow && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
                {previewRow.imagen ? (
                  <img src={assetUrl(previewRow.imagen)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">Sin portada</div>
                )}
              </div>
              {Array.isArray(previewRow.galeria) && previewRow.galeria.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {previewRow.galeria.map((p) => (
                    <img key={p} src={assetUrl(p)} alt="" className="aspect-video rounded-lg object-cover ring-1 ring-white/15" />
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-lime">{previewRow.ubicacion}</p>
              <h3 className="font-display text-3xl font-bold text-white">{previewRow.titulo}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{previewRow.descripcion}</p>
              <p className="font-display text-2xl font-bold text-accent-lime">
                {new Intl.NumberFormat('es-CO', {
                  style: 'currency',
                  currency: 'COP',
                  maximumFractionDigits: 0,
                }).format(Number(previewRow.precio))}
              </p>
              <p className="text-xs text-slate-500">
                Ventana: {previewRow.fecha_inicio} → {previewRow.fecha_fin}
              </p>
              <VacacionMapEmbed
                mapaEmbed={previewRow.mapa_embed}
                ubicacionLabel={previewRow.ubicacion}
                className="border-t border-white/10 pt-4"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
